/* eslint-disable no-console */
import io, { Socket } from 'socket.io-client';
import { MutableRefObject, useEffect, useRef } from 'react';

import token from 'utils/token';
import config from 'config';
import {
  addOneEventToNotificationList,
  clearNotificationList,
} from 'store/notifications/reducer';
import { readOwnNotifications } from 'utils/helpers';
import { addRecordToHistoryList } from 'pages/Candidates/store/reducer';
import {
  addOneCompanyToStart,
  setOrderedIdsToStart,
} from 'pages/Companies/store/reducer';
import { addRecordToVacancyHistoryList } from 'pages/Vacancy/store/reducer';
import getNotificationsList from 'store/notifications/thunks';
import { togglePopup, setVacancy } from 'store/discount/reducer';
import { checkDiscount } from 'store/discount/thunks';
import { DefaultEventsMap } from '@socket.io/component-emitter';
import { useAppDispatch, useAppSelector } from './hooks';

let socketInstance: Socket<DefaultEventsMap, DefaultEventsMap> | null = null;

const useSocket = () => {
  const { user } = useAppSelector(({ main }) => main);
  const dispatch = useAppDispatch();
  const reconnectAttempts = useRef(0);

  useEffect(() => {
    if (user && !socketInstance) {
      socketInstance = io(config.baseUrl, {
        auth: {
          token: token.access.get(),
        },
        withCredentials: true,
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
      });

      setupSocketListeners(socketInstance, reconnectAttempts);
    }

    return () => {
      if (!socketInstance) return;
      socketInstance.off('myInfo');
      socketInstance.off('disconnect');
      socketInstance.off('vacancyCreated');
      socketInstance.off('vacancyStatusChanged');
      socketInstance.off('newCandidate');
      socketInstance.off('vacancyShared');
      socketInstance.off('candidateNewComment');
      socketInstance.off('vacancyNewComment');
      socketInstance.off('candidateStatusUpdated');
      socketInstance.off('newClient');
      socketInstance.off('newConsultation');
      socketInstance.off('clearNotifications');
      socketInstance.off('linkedinDiscount');

      socketInstance.off('connect_error');
    };
  }, [dispatch, user]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (socketInstance && !socketInstance.connected) {
          socketInstance.connect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const setupSocketListeners = (socket: Socket<DefaultEventsMap, DefaultEventsMap>, reconnectAttempts: MutableRefObject<number>) => {
    socket.on('connect', () => {
      reconnectAttempts.current = 0;
      dispatch(checkDiscount());
      console.log('Socket connected:', socket.id);
      
      if (user) {
        socket.emit('myInfo', user.id);
      }
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      
      if (reason === 'io server disconnect') {
        socket.connect();
      }
    });

    socket.on('reconnect_attempt', (attempt) => {
      reconnectAttempts.current = attempt;
      console.log(`Reconnection attempt: ${attempt}`);
    });

    socket.on('reconnect_error', (error) => {
      console.log('Reconnection error:', error);
    });

    socket.on('reconnect_failed', () => {
      console.log('Reconnection failed');
      if (socketInstance) {
        socketInstance.disconnect();
        socketInstance = null;
      }
    });

    socket.on('vacancyCreated', (res) => {
      dispatch(
        addOneEventToNotificationList(readOwnNotifications(res.data, user.id)),
      );
    });

    socket.on('vacancyStatusChanged', (res) => {
      dispatch(
        addOneEventToNotificationList(readOwnNotifications(res.data, user.id)),
      );
    });

    socket.on('newCandidate', (res) => {
      dispatch(
        addOneEventToNotificationList(readOwnNotifications(res.data, user.id)),
      );
    });

    socket.on('vacancyShared', (res) => {
      dispatch(
        addOneEventToNotificationList(readOwnNotifications(res.data, user.id)),
      );
    });

    socket.on('candidateNewComment', (res) => {
      dispatch(
        addOneEventToNotificationList(readOwnNotifications(res.data, user.id)),
      );

      dispatch(
        addRecordToHistoryList({
          ...res.data,
          vacancy_id: +res.data.vacancyId,
          candidate_id: +res.data.candidateId,
        }),
      );
    });

    socket.on('vacancyNewComment', (res) => {
      dispatch(
        addOneEventToNotificationList(readOwnNotifications(res.data, user.id)),
      );

      dispatch(
        addRecordToVacancyHistoryList({
          ...res.data,
          vacancy_id: +res.data.vacancyId,
        }),
      );
    });

    socket.on('candidateStatusUpdated', (res) => {
      dispatch(
        addOneEventToNotificationList(readOwnNotifications(res.data, user.id)),
      );
    });

    socket.on('newClient', (res) => {
      dispatch(addOneEventToNotificationList(res.data));
      dispatch(addOneCompanyToStart(res.data.client));
      dispatch(setOrderedIdsToStart(res.data.clientId));
    });

    socket.on('newConsultation', (res) => {
      dispatch(addOneEventToNotificationList(res.data));
    });

    socket.on('clearIrrelevantNotifications', async (res) => {
      await dispatch(getNotificationsList(user.id));
      dispatch(clearNotificationList(res.data));
    });

    socket.on('linkedinDiscount', (res) => {
      dispatch(setVacancy(res.data));
      dispatch(togglePopup(true));
    })

    socket.on('connect_error', (err) => console.log(err));
  };
};

export default useSocket;

export const disconnectSocket = () => {
  if (socketInstance) {
    socketInstance.disconnect();
    socketInstance = null;
  }
};

export const getSocketStatus = () => socketInstance ? socketInstance.connected : false;
